import { createSlice } from "@reduxjs/toolkit";

/* ── Helpers ── */
const STORAGE_KEY = "vb_cart_v1";
const ABANDONED_KEY = "vb_cart_abandoned_ts";
const ABANDONED_TIMEOUT_MS = 15 * 60 * 1000; // 15 min

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function saveToStorage(items) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    if (items.length > 0) {
      localStorage.setItem(ABANDONED_KEY, Date.now().toString());
    } else {
      localStorage.removeItem(ABANDONED_KEY);
    }
  } catch {}
}

/* ── Initial state ── */
const initialState = {
  items: loadFromStorage(),           // [{ product, quantity }]
  isOpen: false,                      // cart dropdown open
  abandonedBannerDismissed: false,    // user dismissed the abandoned cart banner
  checkoutError: null,
};

/* ── Slice ── */
const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addItem: {
      reducer(state, { payload }) {
        const { product, selectedColor, shippingOption, shippingCost } = payload;
        const pid = product.mongo_id || product.id;
        const existing = state.items.find(
          (i) =>
            (i.product.mongo_id || i.product.id) === pid &&
            (i.selectedColor || null) === (selectedColor || null) &&
            (i.shippingOption || 'free') === (shippingOption || 'free')
        );
        if (existing) {
          existing.quantity += 1;
        } else {
          state.items.push({
            product, quantity: 1,
            ...(selectedColor ? { selectedColor } : {}),
            shippingOption: shippingOption || 'free',
            shippingCost: shippingCost || 0,
          });
        }
        saveToStorage(state.items);
      },
      prepare(product, selectedColor, shippingOption, shippingCost) {
        return { payload: { product, selectedColor: selectedColor || null, shippingOption: shippingOption || 'free', shippingCost: shippingCost || 0 } };
      },
    },

    removeItem(state, { payload: productId }) {
      state.items = state.items.filter(
        (i) => (i.product.mongo_id || i.product.id) !== productId
      );
      saveToStorage(state.items);
    },

    updateQuantity(state, { payload: { productId, quantity } }) {
      if (quantity < 1) {
        state.items = state.items.filter(
          (i) => (i.product.mongo_id || i.product.id) !== productId
        );
      } else {
        const item = state.items.find(
          (i) => (i.product.mongo_id || i.product.id) === productId
        );
        if (item) item.quantity = quantity;
      }
      saveToStorage(state.items);
    },

    clearCart(state) {
      state.items = [];
      saveToStorage([]);
    },

    openCart(state) {
      state.isOpen = true;
    },

    closeCart(state) {
      state.isOpen = false;
    },

    toggleCart(state) {
      state.isOpen = !state.isOpen;
    },

    dismissAbandonedBanner(state) {
      state.abandonedBannerDismissed = true;
      localStorage.removeItem(ABANDONED_KEY);
    },

    setCheckoutError(state, { payload }) {
      state.checkoutError = payload;
    },

    clearCheckoutError(state) {
      state.checkoutError = null;
    },
  },
});

/* ── Selectors ── */
export const selectCartItems = (state) => state.cart.items;
export const selectCartCount = (state) =>
  state.cart.items.reduce((sum, i) => sum + i.quantity, 0);
export const selectCartTotal = (state) =>
  state.cart.items.reduce(
    (sum, i) => sum + (i.product.sale_price || 0) * i.quantity,
    0
  );
export const selectCartShippingTotal = (state) =>
  state.cart.items.reduce((sum, i) => sum + (i.shippingCost || 0) * i.quantity, 0);
export const selectCartGrandTotal = (state) =>
  state.cart.items.reduce(
    (sum, i) => sum + ((i.product.sale_price || 0) + (i.shippingCost || 0)) * i.quantity,
    0
  );
export const selectCartOpen = (state) => state.cart.isOpen;
export const selectAbandonedBannerDismissed = (state) =>
  state.cart.abandonedBannerDismissed;
export const selectCheckoutError = (state) => state.cart.checkoutError;

/* ── Abandoned cart check ── */
export function isCartAbandoned() {
  try {
    const ts = localStorage.getItem(ABANDONED_KEY);
    if (!ts) return false;
    return Date.now() - parseInt(ts, 10) > ABANDONED_TIMEOUT_MS;
  } catch {
    return false;
  }
}

export const {
  addItem,
  removeItem,
  updateQuantity,
  clearCart,
  openCart,
  closeCart,
  toggleCart,
  dismissAbandonedBanner,
  setCheckoutError,
  clearCheckoutError,
} = cartSlice.actions;

// export { selectCartShippingTotal, selectCartGrandTotal };

export default cartSlice.reducer;
